/**
 * campDataArtifactFetch — the ONE shared classified fetcher for a camp-data run artifact in GCS,
 * via the signed-URL callable (`getCampDataArtifactUrl`).
 *
 * KB-3290: before this file existed, at least five call sites (`getChangeSet.ts`,
 * `runArtifacts.ts`, `getVariationDecisionTable.ts`, `getProposedNewSessionTable.ts`,
 * `getPriceUpdateProposalTable.ts`) each hand-rolled a near-identical "fetch + parse, return null
 * on ANY failure" helper. Collapsing a genuinely-absent artifact and a FAILED fetch (network / HTTP
 * / JSON-parse error) into the same `null` means a reviewer staring at a broken fetch sees the
 * identical calm "nothing here" as a run that truly produced nothing — the highest-cost bug shape
 * in this codebase, because nothing reports it. Every caller now goes through here, so the
 * classification (and its fix, if it ever needs one) lives in exactly one place.
 */

import { getCampDataArtifactUrl } from "@/services/campDataArtifactService";

/** How one artifact fetch resolved. */
export type ArtifactFetchOutcome = "ok" | "absent" | "error";

/**
 * The result of one {@link fetchJsonArtifactAtRelPath} call. `outcome` distinguishes a genuinely
 * missing artifact (`"absent"` — the callable's `not-found`, meaning nothing was ever uploaded at
 * this path) from a fetch that FAILED (`"error"` — network/HTTP/parse trouble, or any OTHER
 * callable rejection such as `permission-denied`/`unauthenticated`/`invalid-argument`: none of
 * those confirm absence, so none may be silently read as one). `data` is `null` for both non-`"ok"`
 * outcomes.
 */
export interface ArtifactFetchResult<T> {
  data: T | null;
  outcome: ArtifactFetchOutcome;
  relPath: string;
  /** HTTP status from the GCS fetch, when the failure got as far as a response. */
  httpStatus?: number;
}

/**
 * Fetch + parse one JSON artifact from GCS via the signed-URL callable, classified into
 * `ok` / `absent` / `error`. Logs the relPath + HTTP status once per fetch — the only
 * pre-classification signal a caller had before this was a console guess.
 */
export async function fetchJsonArtifactAtRelPath<T>(
  campSlug: string,
  runId: string,
  relPath: string,
): Promise<ArtifactFetchResult<T>> {
  try {
    const { url } = await getCampDataArtifactUrl({ campSlug, runId, relPath, mode: "view" });
    const res = await fetch(url);
    if (!res.ok) {
      console.warn(`[campDataArtifactFetch] artifact fetch failed: relPath=${relPath} httpStatus=${res.status}`);
      return { data: null, outcome: "error", relPath, httpStatus: res.status };
    }
    const data = (await res.json()) as T;
    // KB-3290 round 3 (#4): this fired via console.info on EVERY successful fetch — getChangeSet
    // fans out ~7 of these in parallel and re-runs on every accept/reject/suppress, so a healthy
    // queue-working operator got ~7 new visible lines per action. console.debug still satisfies
    // AC3 ("logged once per fetch") but sits under Chrome DevTools' default-hidden Verbose level,
    // so the signal exists for a diagnosis without drowning the common, healthy case.
    console.debug(`[campDataArtifactFetch] artifact fetched: relPath=${relPath} httpStatus=${res.status}`);
    return { data, outcome: "ok", relPath, httpStatus: res.status };
  } catch (err: unknown) {
    // Any other callable error (invalid-argument, permission-denied, unauthenticated, a network
    // failure reaching the callable) or a JSON parse failure on an ok response — we don't know
    // what's actually at this path, so this is NOT the same as a confirmed absent. Deliberately NOT
    // narrowed to a "transient auth" allowlist that reads as absent: a permission failure silently
    // rendering as "nothing here" is the same defect this file exists to close, one layer up.
    return { data: null, outcome: classifyArtifactFetchError(err, relPath, "artifact"), relPath };
  }
}

/**
 * Fetch + parse a JSONL run artifact into rows, classified the same way as
 * {@link fetchJsonArtifactAtRelPath}. Rows are the parsed lines; `outcome` is `"ok"` even when the
 * file is empty or every line fails to parse (a present-but-garbled/empty file is not the same as a
 * confirmed absent or a fetch error) — callers that need to distinguish "ok with zero rows" from
 * "ok but every line failed to parse" should inspect `rawLineCount`.
 */
export interface JsonlArtifactResult {
  rows: Array<Record<string, unknown>>;
  outcome: ArtifactFetchOutcome;
  relPath: string;
  httpStatus?: number;
  /** Non-blank lines seen in the raw response, before per-line JSON parsing. */
  rawLineCount: number;
}

export async function fetchJsonlArtifactAtRelPath(
  campSlug: string,
  runId: string,
  relPath: string,
): Promise<JsonlArtifactResult> {
  try {
    const { url } = await getCampDataArtifactUrl({ campSlug, runId, relPath, mode: "view" });
    const res = await fetch(url);
    if (!res.ok) {
      console.warn(`[campDataArtifactFetch] jsonl fetch failed: relPath=${relPath} httpStatus=${res.status}`);
      return { rows: [], outcome: "error", relPath, httpStatus: res.status, rawLineCount: 0 };
    }
    const text = await res.text();
    const lines = text
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);
    const rows = lines
      .map((l) => {
        try {
          return JSON.parse(l) as Record<string, unknown>;
        } catch {
          return null;
        }
      })
      .filter((r): r is Record<string, unknown> => r != null);
    // KB-3290 round 3 (#4): same rationale as fetchJsonArtifactAtRelPath's success log — console.debug
    // over console.info so the per-fetch breadcrumb (AC3) doesn't drown the healthy-queue operator.
    console.debug(`[campDataArtifactFetch] jsonl fetched: relPath=${relPath} httpStatus=${res.status} lines=${lines.length}`);
    return { rows, outcome: "ok", relPath, httpStatus: res.status, rawLineCount: lines.length };
  } catch (err: unknown) {
    return { rows: [], outcome: classifyArtifactFetchError(err, relPath, "jsonl"), relPath, rawLineCount: 0 };
  }
}

/**
 * KB-3290 round 2 (#4): the two fetchers' catch blocks classified a caught error identically
 * (confirmed-absent `functions/not-found` vs everything else) but duplicated the logic verbatim —
 * against this file's own stated goal that "the classification ... lives in exactly one place."
 * Extracted here; each caller supplies its own log-line `kind` label ("artifact" / "jsonl") and
 * builds its own return shape (the two differ: `data` vs `rows`/`rawLineCount`).
 *
 * KB-3451: exported, because a third caller now classifies the SAME rejection without downloading
 * anything — `campDataRunLiveness`'s existence probe, which asks the signed-URL callable and stops
 * there. It needs the one code that confirms absence, and hand-writing `"functions/not-found"` a
 * third time is how the one-place claim above stops being true.
 */
export function classifyArtifactFetchError(err: unknown, relPath: string, kind: "artifact" | "jsonl"): ArtifactFetchOutcome {
  const code = err && typeof err === "object" && "code" in err ? (err as { code?: unknown }).code : undefined;
  if (code === "functions/not-found") {
    console.info(`[campDataArtifactFetch] ${kind} absent: relPath=${relPath}`);
    return "absent";
  }
  console.warn(`[campDataArtifactFetch] ${kind} fetch error: relPath=${relPath}`, err);
  return "error";
}
